import React, { useEffect, useMemo, useState } from "react";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronDown,
    RefreshCw,
    SearchX,
    SlidersHorizontal,
    UploadCloud,
    UserPlus,
    UsersRound,
} from "lucide-react";

import { DEFAULT_COLUMNS } from "./LeadsTableColumns";
import { DuplicateBadge } from "../LeadDetails/LeadDuplicates";
import LeadPagination from "./LeadPagination";
import BulkActions from "./LeadsTable/BulkActions";
import CustomizeColumnsDropdown from "./LeadsTable/CustomizeColumnsDropdown";
import { formatLeadFieldValue, leadCustomValue } from "../../utils/leadFields";

const ID_WIDTH = 116;
const LEAD_WIDTH = 270;

function getInitials(name) {
    if (!name) return "L";

    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
}

function SortHeader({ label, field, sort, onSort }) {
    const active = sort?.field === field;
    const Icon = active
        ? sort.direction === "asc"
            ? ArrowUp
            : ArrowDown
        : ArrowUpDown;

    return (
        <button
            type="button"
            onClick={() => onSort(field)}
            title={`Sort by ${label}`}
            className={`group inline-flex items-center gap-1.5 rounded-md py-1 text-left text-xs font-bold tracking-[-0.01em] transition ${active
                ? "text-[#236fa3]"
                : "text-[#647d8e] hover:text-[#173044]"
                }`}
        >
            <span>{label}</span>
            <Icon className={`h-3.5 w-3.5 ${active ? "text-[#2f84bd]" : "text-[#afc0c9] group-hover:text-[#647d8e]"}`} />
        </button>
    );
}

function SkeletonBlock({ className = "" }) {
    return <span className={`block animate-pulse rounded-md bg-[#eaf2f5] ${className}`} />;
}

function LeadsTableSkeleton({ visibleColumns }) {
    return Array.from({ length: 8 }, (_, rowIndex) => (
        <tr key={rowIndex} aria-hidden="true" className="bg-white">
            <td
                style={{ left: 0, width: ID_WIDTH, minWidth: ID_WIDTH }}
                className="sticky left-0 z-20 border-b border-r border-[#e5eef2] bg-white px-4 py-4"
            >
                <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-4 w-4 shrink-0" />
                    <SkeletonBlock className={`h-3.5 ${rowIndex % 2 ? "w-11" : "w-14"}`} />
                </div>
            </td>
            <td
                style={{ left: ID_WIDTH, width: LEAD_WIDTH, minWidth: LEAD_WIDTH }}
                className="sticky z-20 border-b border-[#e5eef2] bg-white px-4 py-3.5 after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:w-5 after:translate-x-full after:bg-gradient-to-r after:from-[#dce9ee]/70 after:to-transparent"
            >
                <div className="flex items-center gap-3">
                    <SkeletonBlock className="h-9 w-9 shrink-0 rounded-xl" />
                    <div className="min-w-0 flex-1 space-y-2">
                        <SkeletonBlock className={`h-3.5 ${rowIndex % 3 ? "w-32" : "w-40"}`} />
                        <SkeletonBlock className={`h-2.5 ${rowIndex % 2 ? "w-44" : "w-36"}`} />
                    </div>
                </div>
            </td>
            {visibleColumns.map((column, columnIndex) => (
                <td
                    key={column.id}
                    style={{ width: column.width, minWidth: column.width }}
                    className="border-b border-[#e5eef2] bg-white px-4 py-3.5"
                >
                    <div className="space-y-2">
                        <SkeletonBlock className={`h-3.5 ${columnIndex % 3 === 0 ? "w-28" : columnIndex % 3 === 1 ? "w-20" : "w-32"}`} />
                        <SkeletonBlock className={`h-2.5 ${columnIndex % 2 ? "w-24" : "w-16"}`} />
                    </div>
                </td>
            ))}
        </tr>
    ));
}

function EmptyState({ search, hasAnyLeads, onAddLead, onImportLeads }) {
    const filteredEmpty = Boolean(search || hasAnyLeads);

    return (
        <div className="crm-table-state">
            <div className="crm-state">
                <span className="ws-icon-tile crm-state-icon" aria-hidden="true">
                    {filteredEmpty ? <SearchX size={18} /> : <UsersRound size={18} />}
                </span>
                <h3 className="ws-h3">{filteredEmpty ? "No leads match this view" : "Bring in your first lead"}</h3>
                <p className="ws-muted">
                    {search
                        ? `No results for “${search}”. Try a different search or filter.`
                        : hasAnyLeads
                            ? "No leads match the current view. Change or reset the active filters."
                            : "Add one lead by hand or import your existing list from a spreadsheet."}
                </p>
                {!filteredEmpty && (
                    <div className="crm-state-actions">
                        <button type="button" onClick={onAddLead} className="ws-btn ws-btn--primary"><UserPlus size={15} /> Add lead</button>
                        <button type="button" onClick={onImportLeads} className="ws-btn ws-btn--quiet"><UploadCloud size={15} /> Import leads</button>
                    </div>
                )}
            </div>
        </div>
    );
}

function ErrorState({ message, onRetry }) {
    return (
        <div className="crm-table-state">
            <div className="crm-state">
                <span className="ws-icon-tile ws-icon-tile--amber crm-state-icon" aria-hidden="true"><RefreshCw size={18} /></span>
                <h3 className="ws-h3">Leads could not be loaded</h3>
                <p className="ws-muted">{message}</p>
                <div className="crm-state-actions">
                    <button type="button" onClick={onRetry} className="ws-btn ws-btn--primary"><RefreshCw size={14} /> Retry</button>
                </div>
            </div>
        </div>
    );
}

export default function LeadsTable({
    toolbar,
    leads,
    loading = false,
    error = "",
    workspaceId,
    onLeadClick,
    sort,
    setSort,
    pagination,
    search,
    hasAnyLeads = false,
    onAddLead,
    onImportLeads,
    onPageChange,
    onLimitChange,
    onRetry,
    onDeleted,
    customFields = [],
    starterFields = [],
    pipelineId = "",
}) {
    const preferenceKey = `crm-columns:${workspaceId}:${pipelineId || "workspace"}`;
    const [preferences, setPreferences] = useState({});
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(preferenceKey) || "{}");
            setPreferences(saved && typeof saved === "object" && !Array.isArray(saved) ? saved : {});
        }
        catch { setPreferences({}); }
    }, [preferenceKey]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedLeadIds, setSelectedLeadIds] = useState([]);

    useEffect(() => { setSelectedLeadIds([]); }, [workspaceId, pipelineId, leads]);

    const dynamicColumns = useMemo(() => customFields
        .filter((field) => field.is_active && field.is_table_visible)
        .map((field) => ({
            id: `custom:${field.key}`,
            label: field.label,
            width: 210,
            visible: true,
            sortField: `custom:${field.key}`,
            renderCell: (lead) => {
                const fieldValue = lead.custom_field_values?.find((item) => item.key === field.key);
                const value = leadCustomValue(lead, field.key);
                const text = formatLeadFieldValue(field, value, fieldValue?.user);
                return <span className="crm-cell-main" title={`${field.section || "Custom field"} · ${text}`}>{text}</span>;
            },
        })), [customFields]);
    const columns = useMemo(() => [
        ...starterFields.map((field) => ({
            id: `starter:${field.key}`, label: field.label, width: 210, visible: field.is_table_visible,
            sortField: `starter:${field.key}`,
            renderCell: (lead) => <span className="crm-cell-main" title={lead[field.key] || ""}>{lead[field.key] || "—"}</span>,
        })),
        ...DEFAULT_COLUMNS.filter((column) => !["company", "lead_source"].includes(column.id)),
        ...dynamicColumns,
    ].map((column, index) => ({ ...column, visible: preferences[column.id]?.visible ?? column.visible, order: preferences[column.id]?.order ?? index }))
        .sort((a, b) => a.order - b.order), [starterFields, dynamicColumns, preferences]);
    const visibleColumns = useMemo(() => columns.filter((column) => column.visible), [columns]);
    const enabledKeys = new Set(starterFields.map((field) => field.key));
    const visibleLeadIds = useMemo(
        () => new Set(leads.map((lead) => lead.id)),
        [leads]
    );
    const isAllSelected = leads.length > 0 && leads.every((lead) => selectedLeadIds.includes(lead.id));
    const isSomeSelected = selectedLeadIds.length > 0 && !isAllSelected;
    const tableWidth = useMemo(
        () => ID_WIDTH + LEAD_WIDTH + visibleColumns.reduce((sum, column) => sum + column.width, 0),
        [visibleColumns]
    );

    useEffect(() => {
        setSelectedLeadIds((current) => {
            const nextSelection = current.filter((id) => visibleLeadIds.has(id));
            return nextSelection.length === current.length ? current : nextSelection;
        });
    }, [visibleLeadIds]);

    useEffect(() => {
        const handleReset = () => { localStorage.removeItem(preferenceKey); setPreferences({}); };
        window.addEventListener("columns-reset", handleReset);
        return () => window.removeEventListener("columns-reset", handleReset);
    }, [preferenceKey]);

    const toggleLeadSelection = (leadId) => {
        setSelectedLeadIds((current) =>
            current.includes(leadId)
                ? current.filter((id) => id !== leadId)
                : [...current, leadId]
        );
    };

    const toggleSelectAll = () => {
        setSelectedLeadIds(isAllSelected ? [] : leads.map((lead) => lead.id));
    };

    const saveConfig = (nextColumns) => {
        const next = Object.fromEntries(nextColumns.map((column, order) => [column.id, { visible: column.visible, order }]));
        setPreferences(next);
        try { localStorage.setItem(preferenceKey, JSON.stringify(next)); } catch { /* Keep session preferences when storage is full. */ }
    };

    const resetConfig = () => { localStorage.removeItem(preferenceKey); setPreferences({}); };

    const handleSort = (field) => {
        setSort((current) => ({
            field,
            direction: current?.field === field && current?.direction === "asc" ? "desc" : "asc",
        }));
    };

    return (
        <section
            aria-label="Leads table"
            aria-busy={loading}
            className="ws-card crm-leads-table relative w-full min-w-0 max-w-full"
        >
            <div className="crm-table-toolbar">
                {toolbar && <div className="crm-table-search">{toolbar}</div>}
                <div className={toolbar ? "sr-only" : "crm-table-status"}>
                    <p>
                        {loading
                            ? "Loading leads…"
                            : selectedLeadIds.length > 0
                                ? `${selectedLeadIds.length} lead${selectedLeadIds.length === 1 ? "" : "s"} selected`
                                : `${pagination.total || 0} lead${pagination.total === 1 ? "" : "s"} in this view`}
                    </p>
                </div>

                <div className="ws-dd-root">
                    <button
                        type="button"
                        disabled={loading}
                        onClick={() => setIsDropdownOpen((open) => !open)}
                        aria-label="Columns"
                        aria-expanded={isDropdownOpen}
                        className="ws-sort"
                    >
                        <SlidersHorizontal size={14} className="ws-sort-icon" />
                        <span>Columns</span>
                        <ChevronDown size={14} className="ws-dd-chev" />
                    </button>

                    <CustomizeColumnsDropdown
                        isOpen={isDropdownOpen}
                        onClose={() => setIsDropdownOpen(false)}
                        columns={columns}
                        onSave={saveConfig}
                        onReset={resetConfig}
                    />
                </div>
            </div>

            <div className="crm-lead-scroll custom-scrollbar">
                {!loading && error ? <ErrorState message={error} onRetry={onRetry} /> : !loading && leads.length === 0 ? <EmptyState search={search} hasAnyLeads={hasAnyLeads} onAddLead={onAddLead} onImportLeads={onImportLeads} /> : (
                <table style={{ minWidth: tableWidth }} className="w-full table-fixed border-separate border-spacing-0">
                    <thead>
                        <tr>
                            <th
                                style={{ left: 0, width: ID_WIDTH, minWidth: ID_WIDTH }}
                                className="sticky left-0 top-0 z-30 h-12 border-b border-r border-[#d6e5eb] bg-[#f3f8fa] px-4 text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        aria-label="Select all leads on this page"
                                        checked={isAllSelected}
                                        disabled={loading || leads.length === 0}
                                        ref={(element) => {
                                            if (element) element.indeterminate = isSomeSelected;
                                        }}
                                        onChange={toggleSelectAll}
                                        onClick={(event) => event.stopPropagation()}
                                        className="h-4 w-4 rounded border-[#afc4ce] text-[#2f84bd] focus:ring-[#50b8e7] disabled:opacity-40"
                                    />
                                    <SortHeader label="ID" field="id" sort={sort} onSort={handleSort} />
                                </div>
                            </th>

                            <th
                                style={{ left: ID_WIDTH, width: LEAD_WIDTH, minWidth: LEAD_WIDTH }}
                                className="sticky top-0 z-30 h-12 border-b border-[#d6e5eb] bg-[#f3f8fa] px-4 text-left after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:w-5 after:translate-x-full after:bg-gradient-to-r after:from-[#cfdfe6]/65 after:to-transparent"
                            >
                                <SortHeader label="Lead" field="name" sort={sort} onSort={handleSort} />
                            </th>

                            {visibleColumns.map((column) => (
                                <th
                                    key={column.id}
                                    style={{ width: column.width, minWidth: column.width }}
                                    className="sticky top-0 z-20 h-12 border-b border-[#d6e5eb] bg-[#f3f8fa] px-4 text-left text-xs font-bold tracking-[-0.01em] text-[#647d8e]"
                                >
                                    {column.sortField ? (
                                        <SortHeader
                                            label={column.label}
                                            field={column.sortField}
                                            sort={sort}
                                            onSort={handleSort}
                                        />
                                    ) : column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <LeadsTableSkeleton visibleColumns={visibleColumns} />
                        ) : (
                            leads.map((lead) => (
                                <tr
                                    key={lead.id}
                                    data-selected={selectedLeadIds.includes(lead.id)}
                                    onClick={() => onLeadClick?.(lead.id)}
                                    className="group cursor-pointer bg-white outline-none transition-colors hover:bg-[#f5fafc] focus-within:bg-[#f5fafc]"
                                >
                                    <td
                                        style={{ left: 0, width: ID_WIDTH, minWidth: ID_WIDTH }}
                                        className="sticky left-0 z-20 border-b border-r border-[#e5eef2] bg-white px-4 py-3.5 transition-colors group-hover:bg-[#f5fafc]"
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${lead.name || `lead ${lead.id}`}`}
                                                checked={selectedLeadIds.includes(lead.id)}
                                                onChange={() => toggleLeadSelection(lead.id)}
                                                onClick={(event) => event.stopPropagation()}
                                                className="h-4 w-4 rounded border-[#afc4ce] text-[#2f84bd] focus:ring-[#50b8e7]"
                                            />
                                            <span className="crm-lead-id ws-num">
                                                #{lead.lead_id || lead.id}
                                            </span>
                                        </div>
                                    </td>

                                    <td
                                        style={{ left: ID_WIDTH, width: LEAD_WIDTH, minWidth: LEAD_WIDTH }}
                                        className="sticky z-20 border-b border-[#e5eef2] bg-white px-4 py-3 transition-colors after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:w-5 after:translate-x-full after:bg-gradient-to-r after:from-[#dce9ee]/65 after:to-transparent group-hover:bg-[#f5fafc]"
                                    >
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onLeadClick?.(lead.id);
                                            }}
                                            className="flex w-full min-w-0 items-center gap-3 text-left outline-none"
                                        >
                                            <span className="crm-lead-avatar" data-tone={Number(lead.id) % 4}>
                                                {getInitials(lead.name)}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[15px] font-bold text-[#173044] transition-colors group-hover:text-[#236fa3]">
                                                    {lead.name || "Unnamed lead"}
                                                </span>
                                                <DuplicateBadge count={Number(lead.duplicate_count || 0)} />
                                                <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[13px] text-[#6d8493]">
                                                    <span className="truncate">{(enabledKeys.has("email") && lead.email) || (enabledKeys.has("phone") && lead.phone) || "—"}</span>
                                                    {enabledKeys.has("email") && enabledKeys.has("phone") && lead.email && lead.phone && (
                                                        <>
                                                            <span className="text-[#bfd0d8]">•</span>
                                                            <span className="shrink-0">{lead.phone}</span>
                                                        </>
                                                    )}
                                                </span>
                                            </span>
                                        </button>
                                    </td>

                                    {visibleColumns.map((column) => (
                                        <td
                                            key={column.id}
                                            style={{ width: column.width, minWidth: column.width }}
                                            className="border-b border-[#e5eef2] bg-white px-4 py-3 transition-colors group-hover:bg-[#f5fafc]"
                                        >
                                            {column.renderCell(lead)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>)}
            </div>

            <div className="crm-bulk-dock">
                    {!loading && (
                        <BulkActions
                            canDelete={selectedLeadIds.every(id => leads.find(lead => lead.id === id)?.permissions?.delete)}
                            onDeleted={onDeleted || onRetry}
                            selectedLeadIds={selectedLeadIds}
                            setSelectedLeadIds={setSelectedLeadIds}
                            workspaceId={workspaceId}
                        />
                    )}

            </div>

            <div className="crm-table-foot">
                {loading ? (
                    <div className="flex h-9 items-center justify-between" aria-hidden="true">
                        <SkeletonBlock className="h-3 w-28" />
                        <div className="flex gap-2">
                            <SkeletonBlock className="h-8 w-20" />
                            <SkeletonBlock className="h-8 w-24" />
                        </div>
                    </div>
                ) : (
                    <LeadPagination
                        pagination={pagination}
                        search={search}
                        onPageChange={onPageChange}
                        onLimitChange={onLimitChange}
                    />
                )}
            </div>
        </section>
    );
}
